
import React, { useState, useEffect } from "react";
import { Film, VideoOff } from "lucide-react";

interface VideoPageProps {
  content: {
    url: string;
    autoplay: boolean;
    muted: boolean;
    controls: boolean;
    loop: boolean;
    aspect: string;
  };
}

const VideoPage: React.FC<VideoPageProps> = ({ content }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get aspect ratio class based on the selected aspect ratio
  const getAspectRatioClass = () => {
    switch (content.aspect) {
      case "16:9":
        return "aspect-video"; // 16:9
      case "4:3":
        return "aspect-[4/3]"; // 4:3
      case "1:1":
        return "aspect-square"; // 1:1
      case "9:16":
        return "aspect-[9/16]"; // 9:16 (vertical video)
      default:
        return "aspect-video"; // Default to 16:9
    }
  };

  // Function to extract video ID from YouTube or Vimeo URLs
  const getEmbedUrl = (url: string): string => {
    if (!url) return "";
    
    // YouTube URL patterns
    const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/]+)/;
    const youtubeMatch = url.match(youtubeRegex);
    
    if (youtubeMatch && youtubeMatch[1]) {
      const params = new URLSearchParams();
      if (content.autoplay) params.append("autoplay", "1");
      if (content.muted) params.append("mute", "1");
      if (content.controls) params.append("controls", "1");
      if (content.loop) params.append("loop", "1");
      
      const queryString = params.toString() ? `?${params.toString()}` : "";
      return `https://www.youtube.com/embed/${youtubeMatch[1]}${queryString}`;
    }
    
    // Vimeo URL patterns
    const vimeoRegex = /^(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/)([0-9]+)/;
    const vimeoMatch = url.match(vimeoRegex);
    
    if (vimeoMatch && vimeoMatch[1]) {
      const params = new URLSearchParams();
      if (content.autoplay) params.append("autoplay", "1");
      if (content.muted) params.append("muted", "1");
      if (content.loop) params.append("loop", "1");
      
      const queryString = params.toString() ? `?${params.toString()}` : "";
      return `https://player.vimeo.com/video/${vimeoMatch[1]}${queryString}`;
    }
    
    // If it's a direct video file URL
    if (url.match(/\.(mp4|webm|ogg)$/i)) {
      return url;
    }
    
    // Default case - return original URL
    return url;
  };

  const isEmbeddedVideo = (url: string): boolean => {
    return url.includes("youtube.com/embed") || url.includes("player.vimeo.com");
  };

  const handleVideoLoad = () => {
    setIsLoading(false);
  };

  const handleVideoError = () => {
    setIsLoading(false);
    setError("שגיאה בטעינת הוידאו. אנא בדקו את כתובת ה-URL ונסו שנית.");
  };

  useEffect(() => {
    // Reset states when content changes
    setIsLoading(true);
    setError(null);
  }, [content.url]);

  const videoUrl = getEmbedUrl(content.url);
  const isEmbedded = isEmbeddedVideo(videoUrl);

  if (!content.url) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-gray-100 rounded-lg text-center">
        <VideoOff size={64} className="text-gray-400 mb-4" />
        <p className="text-xl font-medium text-gray-500">לא הוגדרה כתובת וידאו</p>
        <p className="text-gray-400">אנא הגדירו כתובת URL בעורך הדף</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-red-50 rounded-lg text-center">
        <VideoOff size={64} className="text-red-400 mb-4" />
        <p className="text-xl font-medium text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="max-w-4xl mx-auto">
        {isLoading && (
          <div className="flex justify-center items-center bg-gray-100 rounded-lg p-12">
            <Film size={64} className="animate-pulse text-synagogue-blue" />
          </div>
        )}
        
        <div className={`${isLoading ? 'hidden' : 'block'} overflow-hidden rounded-lg shadow-lg ${getAspectRatioClass()}`}>
          {isEmbedded ? (
            <iframe
              src={videoUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={handleVideoLoad}
              onError={handleVideoError}
            />
          ) : (
            <video
              src={videoUrl}
              className="w-full h-full object-cover"
              controls={content.controls}
              autoPlay={content.autoplay}
              muted={content.muted}
              loop={content.loop}
              onLoadedData={handleVideoLoad}
              onError={handleVideoError}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPage;
